import { cloneDeep } from 'lodash-es';
import { slugify, type TealiumData } from '@i22team/td-longtail-vue-components';

import { useRuntimeConfig } from '#imports';

const TIMEOUT = 1000;
const MAX_ITERATIONS = 10;

let trackingTimeout: ReturnType<typeof setTimeout> | null = null;
let trackingQueue: { action: 'view' | 'link'; data: TealiumData }[] = [];

const waitForUtag = (iteration = 0): Promise<void> =>
  new Promise((resolve, reject) => {
    if (iteration > MAX_ITERATIONS) {
      reject(new Error(`Tealium not available within ${TIMEOUT * MAX_ITERATIONS} ms`));
      return;
    }

    if (window.utag) {
      resolve();
      return;
    }
    trackingTimeout = setTimeout(() => {
      waitForUtag(iteration + 1)
        .then(resolve)
        .catch(reject);
    }, TIMEOUT);
  });

const consumeQueue = async (): Promise<void> => {
  const config = useRuntimeConfig();

  if (trackingTimeout) {
    clearTimeout(trackingTimeout);
  }

  try {
    await waitForUtag();

    trackingQueue.forEach(({ action, data }) => {
      if (!['production', 'test'].includes(config.public.applicationEnv)) {
        console.warn('Tealium', action, data);
      }
      window.utag?.[action](data);
    });
    trackingQueue = [];
  } catch (error) {
    console.error(error);
  }
};

function track(action: 'view' | 'link', data: TealiumData): Promise<void> {
  try {
    // We need to clone data as Tealium manipulates the object and therefore may
    // be able to change data in our components!
    const clonedData = cloneDeep(data);
    ['page_content_id', 'wt_link_id'].forEach((key) => {
      if (!clonedData[key]) return;
      clonedData[key] = slugify(clonedData[key], true);
    });

    trackingQueue.push({
      action,
      data: clonedData,
    });
    return consumeQueue();
  } catch (error) {
    console.info(error);
    return Promise.resolve(); // always resolve. We do not want to crash the whole page!
  }
}

export const tealiumView = (data: TealiumData) => track('view', data);

export const tealiumLink = (data: TealiumData | string) => {
  if (typeof data === 'string') {
    return track('link', { wt_link_id: data });
  }
  return track('link', data);
};

export const updateAutomaticLinkTracking = () => {
  if (window.wt) {
    window.wt.linkTrackInit();
  }
  if (window.wtm) {
    window.wtm.linkTrackInit();
  }
};

const index = {
  view: tealiumView,
  link: tealiumLink,
  updateAutomaticLinkTracking,
};

export default index;
